import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAlert } from "../confirm/ConfirmProvider";
import { useUser } from "../contexts/UserContext";
import { changeMyPassword, deleteSession } from "../fetch/UserRequest";
import { checkSpace } from "../function/checkSpace";
import styles from "../styles/modules/PasswordChangePopup.module.css";
import { toast } from "../toast/toast";
import RequiredMark from "./RequiredMark";

interface PasswordChangePopupProps {
  onClose: () => void;
}

function PasswordChangePopup({ onClose }: PasswordChangePopupProps) {
  const { user, setUser } = useUser();
  const showAlert = useAlert();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    if (!current || !next) {
      toast.error("パスワードを入力してください。");
      return;
    }
    if (next.length < 8 || next.length > 32) {
      toast.error("新しいパスワードは8〜32文字で入力してください。");
      return;
    }
    if (checkSpace([next])) {
      toast.error("パスワードに空白を含めないでください。");
      return;
    }
    if (next !== confirm) {
      toast.error("新しいパスワードと確認用パスワードが一致しません。");
      return;
    }

    setLoading(true);
    try {
      await changeMyPassword(current, next);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "パスワードの変更に失敗しました。",
      );
      setLoading(false);
      return;
    }
    setLoading(false);

    // 変更成功：再ログインを促すダイアログを表示し、OKでログイン画面へ。
    await showAlert(
      "パスワードを変更しました。セキュリティのため、もう一度ログインしてください。",
      { title: "パスワード変更", closeText: "ログイン画面へ" },
    );

    // 現在のセッションを破棄してログイン画面へ遷移する。
    try {
      if (user) await deleteSession({ user });
    } catch {
      // セッション削除に失敗してもローカルは必ずクリアする
    }
    localStorage.removeItem("sessionId");
    setUser(null);
    navigate("/login");
  }

  return (
    <div className={styles["overlay"]} onClick={onClose}>
      <div
        className={styles["panel"]}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <h3 className={styles["title"]}>パスワード変更</h3>
        <form onSubmit={handleSubmit}>
          <label className={styles["field"]}>
            <span>
              現在のパスワード <RequiredMark />
            </span>
            <input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="off"
              required
            />
          </label>
          <label className={styles["field"]}>
            <span>
              新しいパスワード <RequiredMark />
            </span>
            <input
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              minLength={8}
              maxLength={32}
              placeholder="8〜32文字で入力してください"
              autoComplete="off"
              required
            />
          </label>
          <label className={styles["field"]}>
            <span>
              新しいパスワード（確認） <RequiredMark />
            </span>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              minLength={8}
              maxLength={32}
              placeholder="もう一度入力してください"
              autoComplete="off"
              required
            />
          </label>
          <div className={styles["buttons"]}>
            <button
              type="button"
              className={styles["cancel"]}
              onClick={onClose}
              disabled={loading}
            >
              キャンセル
            </button>
            <button
              type="submit"
              className={styles["submit"]}
              disabled={loading}
            >
              変更する
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default PasswordChangePopup;
